import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { Spinner } from '@pearsontechnology/aero-components-learnspace';
import { recordPage as recordPageInfo } from '@pearsontechnology/bronte-lib-fullstory';

import CoursewareItemProvider from 'services/coursewareItem/CoursewareItemProvider';
import { resolveEntryPoint } from 'services/coursewareItem/graphQLResolvers';
import invokePlugin from 'services/lifecycle/invokePlugin';
import { removePluginInstance } from 'services/lifecycle';
import NotFoundPage from 'components/NotFoundPage';
import DataMissingPage from 'components/DataMissingPage';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { getElementIdFromPathname } from 'services/route/locationTranslators';
import { pendoInitialize } from 'cookies/Pendo';

import { FullHeightWrapper, SpinnerWrapper } from './styles';
import { subscribeToEvent } from '../../services/EventService/EventServiceProvider';
import { ACTIVITY } from '../../services/coursewareItem/coursewareTypes';
import CoursewareTargetProvider from '../../services/coursewareTarget/CoursewareTargetProvider';
import { INTERACTIVELOADED, PLUGINREMOVED } from '../../services/lifecycle/lifecycleEvents';

const loadingStates = {
  DONE: 'DONE',
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  DATA_MISSING: 'DATA_MISSING',
};

class PluginContainer extends React.Component {
  constructor(props) {
    super(props);
    this.unsubscribeRemovePluginEvent = undefined;
    this.unsubscribeInteractiveLoadedEvent = undefined;
    this.containerRef = React.createRef();
    this.state = {
      coursewareItemId: null,
      plugin: undefined,
      loadingDeployment: loadingStates.LOADING,
      title: undefined,
    };
  }

  componentDidMount() {
    this.unsubscribeRemovePluginEvent = subscribeToEvent(PLUGINREMOVED, (event) => {
      CoursewareItemProvider.cleanPluginSubscriptions(event.detail.instanceId);
    });
    this.unsubscribeInteractiveLoadedEvent = subscribeToEvent(INTERACTIVELOADED, (event) => {
      this.interactiveLoaded(event.detail.instanceId);
    });
    this.loadDeploymentData();
  }

  componentDidUpdate(prevProps) {
    const { cohortId, deploymentId } = this.props;
    if (cohortId !== prevProps.cohortId || deploymentId !== prevProps.deploymentId) {
      this.loadDeploymentData();
    }

    const { loadingDeployment, coursewareItemId, plugin, title } = this.state;

    if (loadingDeployment === loadingStates.DONE && title === undefined) {
      this.renderPlugin(coursewareItemId, plugin);
      pendoInitialize(deploymentId, coursewareItemId);
    }
  }

  componentWillUnmount() {
    const { coursewareItemId } = this.state;
    removePluginInstance(coursewareItemId);
    if (this.unsubscribeRemovePluginEvent) { this.unsubscribeRemovePluginEvent(); }
    if (this.unsubscribeInteractiveLoadedEvent) { this.unsubscribeInteractiveLoadedEvent(); }
  }


  /**
   * This function is being triggered every time an interactive is loaded, and it's combining
   * its title with the title of the first ancestry activity
   * @param {int} coursewareItemId
   */
  interactiveLoaded = async (coursewareItemId) => {
    const screenTitle = await CoursewareItemProvider.getInCoursewareState(coursewareItemId, ['title']);
    const { ancestry } = await CoursewareTargetProvider.getCoursewareTypeAndAncestry(coursewareItemId);
    const { coursewareItemId: rootCoursewareItemId } = this.state;
    const lesson = ancestry
      ? ancestry.find((element) => element.elementType === ACTIVITY) : undefined;
    const lessonTitle = lesson
      ? await CoursewareItemProvider.getInCoursewareState(lesson.elementId, ['title']) : undefined;

    const rootTitle = lesson
      ? await CoursewareItemProvider.getInCoursewareState(rootCoursewareItemId, ['title']) : undefined;

    if (screenTitle && lessonTitle) {
      const title = `${lessonTitle} - ${screenTitle}`;
      const { cohortId, deploymentId } = this.props;
      const pageName = lesson?.elementId !== rootCoursewareItemId ? `${rootTitle}: ${title}` : title;
      if (window.FS) {
        recordPageInfo(pageName, {
          lesson: lessonTitle,
          screenTitle,
          screenId: coursewareItemId,
          lessonId: lesson?.elementId,
          cohortId,
          deploymentId,
        });
      }
      this.setState({
        title: `${lessonTitle} - ${screenTitle}`,
      });
    }
  };

  loadDeploymentData = async () => {
    const { cohortId, deploymentId, location } = this.props;
    const isSolution1POCEnabled = featureFlagService.is(featureKeys.LEARNSPACE_SOLUTION_1_POC);

    this.setState({
      coursewareItemId: null,
      plugin: undefined,
      loadingDeployment: loadingStates.LOADING,
    });


    let deploymentData;
    const elementId = getElementIdFromPathname(location?.pathname);
    try {
      if (isSolution1POCEnabled && elementId) {
        const query = new URLSearchParams(location.search);
        const courseId = query.get('course');
        const unitId = query.get('unit');
        const lessonId = query.get('lesson');
        const screenId = query.get('screen');

        deploymentData = await CoursewareItemProvider.loadDeploymentDataNew({
          cohortId,
          deploymentId,
          elementId,
          courseId,
          unitId,
          lessonId,
          screenId,
          disableBrowserHistory: false,
        });
      } else if (isSolution1POCEnabled && !elementId) {
        deploymentData = await CoursewareItemProvider.loadDeploymentDataNew({
          cohortId,
          deploymentId,
          disableBrowserHistory: false,
        });
      } else {
        deploymentData = await CoursewareItemProvider.loadDeploymentData({ cohortId, deploymentId, disableBrowserHistory: false });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load deployment data', error);

      this.setState({
        loadingDeployment: loadingStates.DATA_MISSING,
      });
      return;
    }

    const { coursewareItemId, plugin } = deploymentData;
    if (coursewareItemId) {
      this.setState({
        coursewareItemId,
        plugin,
        loadingDeployment: loadingStates.DONE,
      });
    } else {
      this.setState({
        coursewareItemId: null,
        plugin: undefined,
        loadingDeployment: loadingStates.ERROR,
      });
    }
  };

  renderPlugin = async (coursewareItemId, plugin) => {
    const element = this.containerRef.current;
    const entryPoint = resolveEntryPoint({ plugin });
    invokePlugin(coursewareItemId, entryPoint, element);
  };

  render() {
    const { loadingDeployment, title } = this.state;

    if (loadingDeployment === loadingStates.LOADING) {
      return (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      );
    }

    if (loadingDeployment === loadingStates.ERROR) {
      return <NotFoundPage />;
    }

    if (loadingDeployment === loadingStates.DATA_MISSING) {
      return <DataMissingPage />;
    }

    return (
      <>
        { title && <Helmet><title>{title}</title></Helmet> }
        <FullHeightWrapper ref={this.containerRef} />
      </>
    );
  }
}

PluginContainer.propTypes = {
  cohortId: PropTypes.string,
  deploymentId: PropTypes.string,
};
export default PluginContainer;
